<template>
  <div class="marketplace-checkout--form-wrapper">
    <div class="flex flex-col items-stretch justify-start gap-md sm:flex-row">
      <SloField class="flex-1">
        <SloLabel :error="errorsFormatted.firstName">{{ 'Full Name' | t }}</SloLabel>
        <SloInput type="text" name="firstName" v-model="address.firstName" :error="errorsFormatted.firstName" />
      </SloField>
      <SloField v-if="address.hasOwnProperty('attention')" class="flex-1">
        <SloLabel :error="errorsFormatted.attention">{{ 'Attention (optional)' | t }}</SloLabel>
        <SloInput type="text" name="attention" v-model="address.attention" :error="errorsFormatted.attention" />
      </SloField>
    </div>
    <div class="flex flex-col items-stretch justify-start gap-md sm:flex-row">
      <SloField class="flex-1">
        <SloLabel :error="errorsFormatted.companyName">{{ 'Company Name (optional)' | t }}</SloLabel>
        <SloInput type="text" name="companyName" v-model="address.companyName" :error="errorsFormatted.companyName" />
      </SloField>
      <SloField class="flex-1">
        <SloLabel :error="errorsFormatted.phone">{{ 'Phone (optional)' | t }}</SloLabel>
        <SloInput type="text" name="phone" v-model="address.phone" :error="errorsFormatted.phone" />
      </SloField>
    </div>
    <SloField>
      <SloLabel :error="errorsFormatted.country?.isocode">{{ 'Country' | t }}</SloLabel>
      <SloSelect
        name="country"
        v-model="address.country.isocode"
        :tool-tip="addToolTip"
        :tool-tip-message="toolTipMessage"
        :error="errorsFormatted.country?.isocode"
      >
        <option
          v-for="(country, index) in countries"
          v-html="country.name"
          :key="`country-${index}`"
          :value="country.isocode"
          :selected="address.country.isocode === country.isocode"
        />
      </SloSelect>
    </SloField>
    <SloField>
      <SloLabel :error="errorsFormatted.line1">{{ 'Address' | t }}</SloLabel>
      <SloInput type="text" name="line1" v-model="address.line1" :error="errorsFormatted.line1" />
      <SloInput type="text" name="line2" v-model="address.line2" :error="errorsFormatted.line2" />
    </SloField>
    <div class="flex flex-col items-stretch justify-start gap-md sm:flex-row">
      <div
        v-for="(addressMetaField, index) in currentCountryAddressMetaFields?.fields"
        :key="`country-address-meta-field-${index}`"
        class="flex-1"
      >
        <SloField v-if="addressMetaField.type === 'town'">
          <SloLabel v-html="addressMetaField.label" :error="errorsFormatted.town" />
          <SloInput
            type="text"
            name="town"
            v-model="address.town"
            :error="errorsFormatted.town"
            class="mt-sm"
          />
        </SloField>
        <SloField v-if="addressMetaField.type === 'region'">
          <SloLabel v-html="addressMetaField.label" :error="errorsFormatted.region?.isocode" />
          <SloSelect
            name="region"
            v-model="address.region.isocode"
            :error="errorsFormatted.region?.isocode"
            class="mt-sm"
          >
            <option
              v-for="(region, index) in regions"
              v-html="region.name"
              :key="`region-${index}`"
              :value="region.isocode"
              :selected="address.region.isocode === region.isocode"
            />
          </SloSelect>
        </SloField>
        <SloField v-if="addressMetaField.type === 'postalCode'">
          <SloLabel v-html="addressMetaField.label" :error="errorsFormatted.postalCode" />
          <SloInput
            type="text"
            name="postalCode"
            v-model="address.postalCode"
            :error="errorsFormatted.postalCode"
            class="mt-sm"
          />
        </SloField>
      </div>
    </div>
  </div>
</template>

<script>
import t from '../../main/i18n';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';

export default {
  name: 'AddressFormFields',
  components: {
    SloField,
    SloLabel,
    SloInput,
    SloSelect
  },
  filters: {
    t
  },
  props: {
    countries: {type: Array, required: true},
    regions: {type: Array, required: true},
    originalAddress: {type: Object, required: true},
    errors: {type: Array, required: false},
    addToolTip: {type: Boolean, required: false},
    toolTipMessage: {type: String, required: false}
  },
  data() {
    return {
      address: {},
      errorsFormatted: {},
      countryAddressMetaFields: [
        {
          isocode: 'AU',
          fields: [
            {type: 'town', label: t('Suburb')},
            {type: 'region', label: t('State/territory')},
            {type: 'postalCode', label: t('Postcode')}
          ]
        },
        {
          isocode: 'CA',
          fields: [
            {type: 'town', label: t('City')},
            {type: 'region', label: t('Province')},
            {type: 'postalCode', label: t('Postal code')}
          ]
        },
        {
          isocode: 'MX',
          fields: [
            {type: 'postalCode', label: t('Postal code')},
            {type: 'town', label: t('City')},
            {type: 'region', label: t('State')}
          ]
        },
        {
          isocode: 'GB',
          fields: [
            {type: 'town', label: t('City')},
            {type: 'region', label: t('Region')},
            {type: 'postalCode', label: t('Postcode')}
          ]
        },
        {
          isocode: 'US',
          fields: [
            {type: 'town', label: t('City')},
            {type: 'region', label: t('State')},
            {type: 'postalCode', label: t('ZIP code')}
          ]
        }
      ]
    };
  },
  created() {
    this.address = this.originalAddress;
  },
  computed: {
    currentCountryAddressMetaFields() {
      return this.countryAddressMetaFields.find(
        rule => this.address.country.isocode === rule.isocode
      );
    }
  },
  watch: {
    address(newValue) {
      this.$emit('addressUpdate', newValue);
    },
    'address.country.isocode'(newValue, oldValue) {
      if (newValue !== oldValue && oldValue !== undefined) {
        this.address.town = '';
        this.address.region.isocode = '';
        this.address.region.isocodeShort = '';
        this.address.postalCode = '';
      }
    },
    'address.region.isocode'(newValue) {
      if (newValue) {
        this.address.region.isocodeShort =
          this.regions.find(region => region.isocode === newValue)?.isocodeShort;
      }
    },
    errors(newValue) {
      this.errorsFormatted = {};
      newValue.map(error => this.errorsFormatted[error.subject] = error.message);
      this.$forceUpdate();
    }
  }
};
</script>
